// src/features/record/pages/RecordPage.tsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Paper,
  Typography,
  Alert,
  Link,
  Divider,
} from "@mui/material";
import { useAuth } from "../../../app/AuthContext";
import { useError } from "../../../app/ErrorContext";
import { useRecordingOpportunity } from "../../../app/RecordingOpportunityContext";
import { useRecording } from "../../../hooks/useRecording";
import { formatTime } from "../../../utils/formatTime";
import { EventHeader } from "./EventHeader";
import { RecordingSection } from "./RecordingSection";

export function RecordPage() {
  const {
    setUuid,
    maxMp3Length,
    authorizedFetch,
    userName,
    eventName,
    eventDate,
    description,
  } = useAuth();
  const { setUploadSuccess } = useRecordingOpportunity();
  const navigate = useNavigate();
  const { showError } = useError();
  const [isUploading, setIsUploading] = useState(false);

  const recording = useRecording({ maxLength: maxMp3Length });

  const handleUpload = async () => {
    if (!recording.mediaBlobUrl) {
      showError("No recording available");
      return;
    }

    setIsUploading(true);
    try {
      const response = await fetch(recording.mediaBlobUrl);
      const blob = await response.blob();

      const formData = new FormData();
      formData.append("file", blob, "recording.mp3");

      const uploadResponse = await authorizedFetch(
        `${process.env.REACT_APP_API_BASE_URL || "http://localhost:8080"}/media/upload`,
        { method: "POST", body: formData },
      );

      if (uploadResponse.ok) {
        setUploadSuccess(true);
        navigate("/");
        setTimeout(() => setUuid(null), 100);
      } else {
        showError("Internal server error");
      }
    } catch (error) {
      console.error("Upload error:", error);
      showError("Internal server error");
    } finally {
      setIsUploading(false);
    }
  };

  if (maxMp3Length === null) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default", py: 4 }}>
        <Container maxWidth="md">
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h4" color="error" sx={{ mb: 2 }}>
              Error
            </Typography>
            <Typography variant="body1" color="text.primary">
              Maximum recording length not available. Please try refreshing the
              page.
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", py: 4 }}>
      <Container maxWidth="md">
        <EventHeader
          userName={userName}
          eventName={eventName}
          eventDate={eventDate}
          description={description}
        />

        <Paper sx={{ p: 4 }}>
          <Typography
            variant="h5"
            gutterBottom
            color="text.primary"
            sx={{ textAlign: "center" }}
          >
            Your Recording
          </Typography>

          <Typography
            variant="body1"
            color="text.primary"
            sx={{ mb: 3, textAlign: "center" }}
          >
            You have <strong>{formatTime(maxMp3Length)}</strong> to record a
            voice message that will play during the race.
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 4, textAlign: "center" }}
          >
            Be encouraging, funny, heartfelt, or anything in between. Your
            message will play in their earbuds when they need it most.
          </Typography>

          <Alert severity="info" sx={{ mb: 4 }}>
            Make sure microphone access is enabled in your browser. If the
            record button doesn't work, check your browser permissions. For
            issues, contact{" "}
            <Link href="mailto:support@runwithmomentum.app" color="primary">
              support@runwithmomentum.app
            </Link>
            .
          </Alert>

          <Divider sx={{ my: 4 }} />

          <RecordingSection
            status={recording.status}
            timeRemaining={recording.timeRemaining}
            maxLength={maxMp3Length}
            mediaBlobUrl={recording.mediaBlobUrl}
            isUploading={isUploading}
            onStart={recording.start}
            onPause={recording.pause}
            onResume={recording.resume}
            onStop={recording.stop}
            onUpload={handleUpload}
            onReset={recording.reset}
          />
        </Paper>
      </Container>
    </Box>
  );
}
